import React from 'react';
import { DockedFooter } from '../../../../../lib/client/components/dockedFooter';
import { Schema } from '../../../../../lib/common/custom-activity/eventTypes';

import { useAppDispatch, useAppSelector } from '../../hooks';
import {
    cancel,
    done,
    idFieldChanged,
    evaluateFieldChanged,
    additionalTextChanged,
} from './simpleConfigurationPageSlice';

/**
 * This is a root page that implements a simple configuration flow.
 */
export function SimpleConfigurationPage() {
    const selectedIdField = useAppSelector(
        (state) => state.simpleConfigurationPage.idField,
    );
    const selectedProfileField = useAppSelector(
        (state) => state.simpleConfigurationPage.evaluateField,
    );
    const selectedAdditionalText = useAppSelector(
        (state) => state.simpleConfigurationPage.additionalText,
    );
    const activity = useAppSelector((state) => state.journey.activity);
    const schema = useAppSelector((state) => state.journey.schema);
    const dirty = useAppSelector(
        (state) => state.simpleConfigurationPage.dirty,
    );
    const dispatch = useAppDispatch();

    if (activity === null || schema === null) {
        return (
            <div className="slds-form slds-m-around--medium">
                Waiting for Journey Builder...
            </div>
        );
    }
    const schemaSelectOptions: JSX.Element[] = getJourneySchemaOptions(schema);

    return (
        <div className="slds-form slds-m-around--medium">
            <div className="scm-common-header-icon slds-m-right_x-small" style={{ marginBottom:'5px'}}>
                <img src="images/agentforce.png" width="40" height="40" /> <img src="images/agentforce_transparent_text.png" width="140" height="35" /> 
            </div>
            <div className="scm-common-header-icon slds-m-right_x-small" style={{ marginBottom:'25px'}}>
                <h2 style={{ fontWeight: 'bold' }}>CUSTOM ACTIVITY CONFIGURATION</h2>
            </div>
            <div className="id-container" style={{ marginBottom:'15px'}}>
                <label
                    className="slds-form-element__label"
                    htmlFor="id-type"
                >
                    Which field holds your SubscriberID values? <span aria-label="required">*</span>
                </label>
                <div className="slds-form-element__control">
                    <div className="slds-select_container">
                        <select
                            className="slds-select"
                            required
                            id="id-type"
                            value={selectedIdField ?? ""}
                            onChange={(event) =>
                                dispatch(
                                    idFieldChanged(
                                        event.target.value,
                                    ),
                                )
                            }
                        >
                            <option value="">&lt; Select a field &gt;</option> {/* Placeholder */}
                            {schemaSelectOptions}
                        </select>
                    </div>
                </div>
            </div>
            <div className="profile-container" style={{ marginBottom:'15px'}}>
                <label
                    className="slds-form-element__label"
                    htmlFor="profile-type"
                >
                    Which field should Agentforce use to evaluate your subscribers? <span aria-label="required">*</span>
                </label>
                <div className="slds-form-element__control">
                    <div className="slds-select_container">
                        <select
                            className="slds-select"
                            required
                            id="profile-type"
                            value={selectedProfileField ?? ""}
                            onChange={(event) =>
                                dispatch(
                                    evaluateFieldChanged(
                                        event.target.value,
                                    ),
                                )
                            }
                        >
                            <option value="">&lt; Select a field &gt;</option> {/* Placeholder */}
                            {schemaSelectOptions}
                        </select>
                    </div>
                </div>
            </div>
            <div className="text-container" style={{ marginBottom:'25px'}}>
                <label
                    className="slds-form-element__label"
                    htmlFor="id-type"
                >
                    Any additional detail(s) for the Agent?
                </label>
                <div className="slds-form-element__control">
                    <input
                        type="text"
                        className="slds-input" 
                        id="text-type"
                        value={selectedAdditionalText || ''}
                        onChange={(event) => dispatch(additionalTextChanged(event.target.value))}
                    />
                </div>
            </div>
            <div className="another-container" style={{ marginBottom:'5px'}}>
                <label
                    className="slds-form-element__label"
                    htmlFor="id-type" style={{ fontStyle: 'italic' }}
                >
                    * Required fields
                </label>
            </div>
            <InfoSection />
            <DockedFooter
                canSubmit={() => dirty}
                cancel={() => dispatch(cancel())}
                done={() =>
                    dispatch(
                        done({
                            activity: activity,
                            schema: schema,
                        }),
                    )
                }
            />
        </div>
    );
}

export function InfoSection(): JSX.Element {
    const activity = useAppSelector((state) => state.journey.activity);
    const schema = useAppSelector((state) => state.journey.schema);

    const entries: JSX.Element[] = [];
    if (activity === null || schema === null) {
        const errorMessage = 'Error loading data from Journey Builder';

        entries.push(
            <div className="errorMessage" key="load_error">
                {errorMessage}
            </div>,
        );
    }

    return <div id="infoSection">{entries}</div>;
}


function getJourneySchemaOptions(schema: Schema): JSX.Element[] {
    const options: JSX.Element[] = [];
    for (let object of schema) {
        options.push(
            <option key={object.key} value={object.key}>
                {object.name}
            </option>,
        );
    }
    return options;
}
